import { useEffect, useMemo, useState } from 'react';
import { CaretRight, CircleNotch, DownloadSimple, Globe, LockSimple, MagnifyingGlass, PencilSimple, WarningCircle, X } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, LeerZustand } from '../../../../components/immobilien/ui';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import { FreigabeDialog } from '../../../../components/dokumente/FreigabeDialog';
import { PapierkorbKnopf } from '../../../../components/dokumente/PapierkorbKnopf';
import type { ToastAktion } from '../../../../components/immobilien/ui';
import type { SuchTrefferAnzeige } from '../../../../lib/dokumente/useAktenschrank';
import {
  FREIGABEN_GEAENDERT, entziehe, ladeDokumenteZuDrive, ladeFreigaben, meldeFreigabenGeaendert, nachDokument, type Freigabe, type FreigabeDok,
} from '../../../../lib/dokumente/freigaben';
import { dateiDarstellung } from './dateiDarstellung';
import { FreigabeAbzeichen } from './OrdnerInhalt';

/** Treffer der globalen Suche (Handoff Dokumente §6.2): Datei, Objekt und Pfad; Inhaltstreffer mit Ausschnitt.
 *  #611: Mehrfachauswahl mit Portal-Freigabe und Herunterladen wie im Ordner (#607). */
export function SuchErgebnisse({ treffer, laedt, fehler, onOeffnen, onHerunterladen, onMeldung, touch = false }: {
  treffer: readonly SuchTrefferAnzeige[];
  laedt: boolean;
  fehler: string | null;
  onOeffnen: (t: SuchTrefferAnzeige) => void;
  onHerunterladen?: (t: SuchTrefferAnzeige) => Promise<void>;
  onMeldung?: (text: string, fehler?: boolean, aktion?: ToastAktion) => void;
  touch?: boolean;
}) {
  const [doks, setDoks] = useState<ReadonlyMap<string, FreigabeDok>>(new Map());
  const [freigaben, setFreigaben] = useState<ReadonlyMap<string, Freigabe[]>>(new Map());
  const [freigabeRunde, setFreigabeRunde] = useState(0);
  const [markiert, setMarkiert] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<null | 'neu' | 'aendern'>(null);
  const [entziehenFrage, setEntziehenFrage] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  // #612: gelöschte Treffer ausblenden (Rückgängig blendet sie wieder ein)
  const [weg, setWeg] = useState<ReadonlySet<string>>(new Set());

  const driveIds = useMemo(() => treffer.map(t => t.id), [treffer]);
  useEffect(() => {
    setMarkiert(alt => new Set([...alt].filter(id => driveIds.includes(id))));
    if (!driveIds.length) { setDoks(new Map()); return; }
    let aktiv = true;
    ladeDokumenteZuDrive(driveIds).then(m => { if (aktiv) setDoks(m); }, () => { if (aktiv) setDoks(new Map()); });
    return () => { aktiv = false; };
  }, [driveIds]);
  useEffect(() => {
    const ids = [...doks.values()].map(d => d.id);
    if (!ids.length) { setFreigaben(new Map()); return; }
    let aktiv = true;
    ladeFreigaben(ids).then(f => { if (aktiv) setFreigaben(nachDokument(f)); }, () => { if (aktiv) setFreigaben(new Map()); });
    return () => { aktiv = false; };
  }, [doks, freigabeRunde]);
  useEffect(() => {
    const neu = () => setFreigabeRunde(r => r + 1);
    window.addEventListener(FREIGABEN_GEAENDERT, neu);
    return () => window.removeEventListener(FREIGABEN_GEAENDERT, neu);
  }, []);

  if (fehler) return <LeerZustand icon={WarningCircle} titel="Suche fehlgeschlagen" text={fehler} />;
  if (laedt && treffer.length === 0) {
    return <div className="immo-glas flex items-center justify-center" style={{ borderRadius: 26, padding: 40 }}><CircleNotch size={24} color="var(--ink3)" className="animate-spin" aria-label="Suche läuft" /></div>;
  }
  const sichtbar = treffer.filter(t => !weg.has(t.id));
  if (sichtbar.length === 0) return <LeerZustand icon={MagnifyingGlass} titel="Keine Treffer" text="Weder im Dateinamen noch im Inhalt gefunden." />;

  const waehlbar = sichtbar.filter(t => doks.has(t.id));
  const alleMarkiert = waehlbar.length > 0 && waehlbar.every(t => markiert.has(t.id));
  const markierte = sichtbar.filter(t => markiert.has(t.id));
  const markierteDoks = markierte.map(t => doks.get(t.id)).filter((d): d is FreigabeDok => !!d);
  const markierteFrei = markierteDoks.filter(d => (freigaben.get(d.id)?.length ?? 0) > 0).length;
  const umschalten = (id: string) => setMarkiert(alt => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const entziehen = async () => {
    setLaeuft(true);
    const f = await entziehe(markierteDoks.map(d => d.id), null);
    setLaeuft(false); setEntziehenFrage(false);
    if (f) { onMeldung?.(`Nicht entzogen: ${f}`, true); return; }
    onMeldung?.('Freigaben entzogen');
    meldeFreigabenGeaendert();
  };

  return (
    <section aria-label="Suchergebnisse" className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 12, gap: 4 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 8, padding: '6px 8px', borderRadius: 16, background: markiert.size ? 'var(--acc-soft)' : 'transparent' }}>
        <AuswahlKaestchen an={alleMarkiert} label="Alle Treffer auswählen" gesperrt={waehlbar.length === 0} touch={touch}
          onUmschalten={() => setMarkiert(alleMarkiert ? new Set() : new Set(waehlbar.map(t => t.id)))} />
        <span className="flex items-center" style={{ gap: 8, fontSize: 12, fontWeight: 700, color: markiert.size ? 'var(--ink)' : 'var(--ink3)' }}>
          {markiert.size ? `${markiert.size} ausgewählt` : `${sichtbar.length} Treffer in allen Objekten · Name und Inhalt`}
          {laedt && <CircleNotch size={13} className="animate-spin" aria-hidden />}
        </span>
        {markiert.size > 0 && (
          <span className="flex flex-wrap items-center" style={{ gap: 6, marginLeft: 'auto' }}>
            <ImmoButton art="akzent" icon={Globe} onClick={() => setDialog('neu')} disabled={!markierteDoks.length}>Freigeben</ImmoButton>
            <ImmoButton icon={PencilSimple} onClick={() => setDialog('aendern')} disabled={!markierteDoks.length}>Freigabe ändern</ImmoButton>
            <ImmoButton icon={LockSimple} onClick={() => setEntziehenFrage(true)} disabled={!markierteFrei}>Entziehen</ImmoButton>
            {onHerunterladen && (
              <ImmoButton icon={DownloadSimple} onClick={() => { void (async () => { for (const t of markierte) await onHerunterladen(t); })(); }}>Herunterladen</ImmoButton>
            )}
            {onMeldung && (
              <PapierkorbKnopf eintraege={markierte.map(t => ({ driveId: t.id, name: t.name, dokumentId: doks.get(t.id)?.id ?? null }))} zeige={onMeldung}
                onFertig={(art, ids) => { setMarkiert(new Set()); setWeg(alt => { const n = new Set(alt); for (const id of ids) { if (art === 'geloescht') n.add(id); else n.delete(id); } return n; }); }} />
            )}
            <ImmoButton icon={X} aria-label="Auswahl aufheben" onClick={() => setMarkiert(new Set())} />
          </span>
        )}
      </div>
      {sichtbar.map(t => {
        const d = dateiDarstellung(t.name, t.mimeType);
        const ort = [t.objekt ? `${t.objekt.nummer} ${t.objekt.name}`.trim() : null, ...(t.pfad ? t.pfad.split('/') : [])].filter(Boolean).join(' › ');
        const oeffenbar = !!t.objekt;
        const dok = doks.get(t.id);
        const an = markiert.has(t.id);
        return (
          // div statt button: das Auswahlkästchen ist selbst ein Knopf
          <div key={t.id} role="button" tabIndex={oeffenbar ? 0 : -1} aria-disabled={!oeffenbar} onClick={() => { if (oeffenbar) onOeffnen(t); }}
            onKeyDown={ev => { if (oeffenbar && (ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); onOeffnen(t); } }}
            title={oeffenbar ? undefined : 'Keinem Objekt zugeordnet'}
            className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer min-w-0"
            style={{ gap: 12, minHeight: 56, padding: '8px 10px', borderRadius: 16, background: an ? 'var(--fill)' : 'transparent', color: 'var(--ink)', opacity: oeffenbar ? 1 : 0.6 }}>
            <AuswahlKaestchen an={an} label={`${t.name} auswählen`} touch={touch} gesperrt={!dok}
              tip={dok ? undefined : 'Nicht in der App erfasst – erst nach dem Abgleich freigebbar'} onUmschalten={() => umschalten(t.id)} />
            <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: d.bg }}><d.icon weight="fill" size={19} color={d.fg} /></span>
            <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</span>
              <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{ort || 'Ablage'}{t.kategorie ? ` · ${t.kategorie}` : ''}</span>
              {t.ausschnitt && <span style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.ausschnitt}</span>}
            </span>
            <FreigabeAbzeichen freigaben={dok ? freigaben.get(dok.id) ?? [] : null} />
            <CaretRight weight="bold" size={13} color="var(--ink3)" style={{ flexShrink: 0 }} />
          </div>
        );
      })}

      {dialog && markierteDoks.length > 0 && (
        <FreigabeDialog dokumente={markierteDoks} aendern={dialog === 'aendern'} onSchliessen={() => setDialog(null)}
          onFertig={m => { setDialog(null); onMeldung?.(m); setMarkiert(new Set()); meldeFreigabenGeaendert(); }} />
      )}
      {entziehenFrage && (
        <BestaetigenDialog titel="Freigaben entziehen?" bestaetigenLabel="Entziehen" icon={LockSimple} laeuft={laeuft}
          text={`${markierteFrei === 1 ? '1 Dokument ist' : `${markierteFrei} Dokumente sind`} danach in keinem Portal mehr sichtbar.`}
          onBestaetigen={() => { void entziehen(); }} onAbbrechen={() => setEntziehenFrage(false)} />
      )}
    </section>
  );
}
